import { createGlobalStyle, DefaultTheme } from 'styled-components';
import theme from './theme';

const GlobalStyles = createGlobalStyle<{ theme: DefaultTheme }>`
  /* Reset and base styles */
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body {
    height: 100%;
    width: 100%;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: ${({ theme }) => theme.colors.background};
  }

  #root {
    height: 100%;
    width: 100%;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${({ theme }) => theme.typography.semibold};
    line-height: ${({ theme }) => theme.typography.lineHeight.tight};
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: ${({ theme }) => theme.spacing[2]};
  }

  h1 {
    font-size: ${({ theme }) => theme.typography['3xl']};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.typography['2xl']};
    }
  }

  h2 {
    font-size: ${({ theme }) => theme.typography['2xl']};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.typography.xl};
    }
  }

  h3 {
    font-size: ${({ theme }) => theme.typography.xl};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.typography.lg};
    }
  }

  h4 {
    font-size: ${({ theme }) => theme.typography.lg};
  }

  h5 {
    font-size: ${({ theme }) => theme.typography.base};
  }

  h6 {
    font-size: ${({ theme }) => theme.typography.sm};
  }

  p {
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  }

  /* Links */
  a {
    text-decoration: none;
    color: inherit;
  }

  /* Form elements */
  input, textarea, select, button {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  button {
    cursor: pointer;
  }

  input, textarea, select {
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing[3]};
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.text};
    transition: ${({ theme }) => theme.transition.fast};

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryBackground};
    }

    &:disabled {
      background-color: ${({ theme }) => theme.colors.gray100};
      color: ${({ theme }) => theme.colors.textSecondary};
      cursor: not-allowed;
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.textLight};
    }
  }

  /* Fix for password fields and form inputs */
  input[type="password"], input[type="text"], input[type="email"] {
    color: ${({ theme }) => theme.colors.text} !important;
    background-color: ${({ theme }) => theme.colors.white} !important;
  }

  /* Fix for mobile menu and navigation text */
  .mobile-menu, .mobile-menu * {
    color: ${({ theme }) => theme.colors.text} !important;
  }

  .mobile-menu button {
    color: ${({ theme }) => theme.colors.text} !important;
    background-color: transparent !important;
  }

  /* Fix for carton cards and project cards */
  .carton-card, .project-card, [class*="Card"] {
    color: ${({ theme }) => theme.colors.text} !important;
    background-color: ${({ theme }) => theme.colors.background} !important;
  }

  .carton-card *, .project-card *, [class*="Card"] * {
    color: inherit !important;
  }

  /* Fix for navigation menu items */
  .nav-item, .nav-link, [class*="Nav"] {
    color: ${({ theme }) => theme.colors.text} !important;
  }

  /* Fix for highlighted/active menu items */
  .nav-item.active, .nav-link.active, [class*="Nav"].active {
    color: ${({ theme }) => theme.colors.white} !important;
    background-color: ${({ theme }) => theme.colors.primary} !important;
  }

  /* Lists */
  ul, ol {
    padding-left: ${({ theme }) => theme.spacing[6]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }

  li {
    margin-bottom: ${({ theme }) => theme.spacing[1]};
  }

  /* Tables */
  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: ${({ theme }) => theme.spacing[6]};
  }

  th, td {
    padding: ${({ theme }) => theme.spacing[3]};
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  th {
    font-weight: ${({ theme }) => theme.typography.semibold};
    background-color: ${({ theme }) => theme.colors.gray50};
    color: ${({ theme }) => theme.colors.text};
  }

  /* Utility classes */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 ${({ theme }) => theme.spacing[4]};

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding: 0 ${({ theme }) => theme.spacing[2]};
    }
  }

  /* Loading animation */
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideInUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideInDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  /* PWA specific styles */
  @media (display-mode: standalone) {
    body {
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);
      padding-left: env(safe-area-inset-left);
      padding-right: env(safe-area-inset-right);
    }
  }

  /* Touch targets for mobile */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    button, a, input, select, textarea {
      min-height: 44px;
      min-width: 44px;
    }
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.gray100};
    border-radius: ${({ theme }) => theme.borderRadius.base};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.gray400};
    border-radius: ${({ theme }) => theme.borderRadius.base};

    &:hover {
      background: ${({ theme }) => theme.colors.gray500};
    }
  }

  /* Selection */
  ::selection {
    background-color: ${({ theme }) => theme.colors.primaryBackground};
    color: ${({ theme }) => theme.colors.primary};
  }

  /* Focus styles for accessibility */
  :focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* Print styles */
  @media print {
    * {
      background: transparent !important;
      color: black !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }

    a, a:visited {
      text-decoration: underline;
    }

    a[href]:after {
      content: " (" attr(href) ")";
    }

    abbr[title]:after {
      content: " (" attr(title) ")";
    }

    .no-print {
      display: none !important;
    }
  }

  /* Status indicator styles */
  .status-indicator {
    display: inline-flex;
    align-items: center;
    padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
    border-radius: ${({ theme }) => theme.borderRadius.full};
    font-size: ${({ theme }) => theme.typography.xs};
    font-weight: ${({ theme }) => theme.typography.medium};
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &.received {
      background-color: ${({ theme }) => theme.colors.successBackground};
      color: ${({ theme }) => theme.colors.successDark};
    }

    &.pending {
      background-color: ${({ theme }) => theme.colors.warningBackground};
      color: ${({ theme }) => theme.colors.warningDark};
    }

    &.in-transit {
      background-color: ${({ theme }) => theme.colors.infoBackground};
      color: ${({ theme }) => theme.colors.infoDark};
    }

    &.missing, &.damaged {
      background-color: ${({ theme }) => theme.colors.errorBackground};
      color: ${({ theme }) => theme.colors.errorDark};
    }

    &.partial {
      background-color: ${({ theme }) => theme.colors.warningBackground};
      color: ${({ theme }) => theme.colors.warningDark};
    }
  }

  /* Card styles */
  .card {
    background-color: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.components.card.borderRadius};
    padding: ${({ theme }) => theme.components.card.padding};
    box-shadow: ${({ theme }) => theme.components.card.boxShadow};
    border: 1px solid ${({ theme }) => theme.colors.border};
    transition: ${({ theme }) => theme.transition.default};

    &:hover {
      box-shadow: ${({ theme }) => theme.shadows.md};
    }
  }

  /* Button styles */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.components.button.padding.md};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-weight: ${({ theme }) => theme.typography.medium};
    font-size: ${({ theme }) => theme.typography.sm};
    line-height: 1;
    transition: ${({ theme }) => theme.transition.fast};
    cursor: pointer;
    border: none;
    text-decoration: none;
    user-select: none;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &.btn-primary {
      background-color: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.white};

      &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.colors.primaryDark};
      }
    }

    &.btn-secondary {
      background-color: ${({ theme }) => theme.colors.gray200};
      color: ${({ theme }) => theme.colors.gray800};

      &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.colors.gray300};
      }
    }

    &.btn-success {
      background-color: ${({ theme }) => theme.colors.success};
      color: ${({ theme }) => theme.colors.white};

      &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.colors.successDark};
      }
    }

    &.btn-danger {
      background-color: ${({ theme }) => theme.colors.error};
      color: ${({ theme }) => theme.colors.white};

      &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.colors.errorDark};
      }
    }

    &.btn-sm {
      padding: ${({ theme }) => theme.components.button.padding.sm};
      font-size: ${({ theme }) => theme.typography.xs};
    }

    &.btn-lg {
      padding: ${({ theme }) => theme.components.button.padding.lg};
      font-size: ${({ theme }) => theme.typography.base};
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }
`;

export default GlobalStyles; 